import React, { useEffect, useState } from 'react';
import { Card, Descriptions, Button, Space, Tag, message, Typography } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { getActivityRuleDetail } from './api';
import type { ActivityRule } from './types';
import dayjs from 'dayjs';
import { contestTypeMapColumn } from '@/pages/vote-activity/vote/type';

const { Title, Paragraph } = Typography;

const STATUS_MAP: Record<number, { text: string; color: string }> = {
  0: { text: '禁用', color: 'default' },
  1: { text: '已启用', color: 'processing' },
};

interface ActivityRuleDetailContentProps {
  detail: ActivityRule;
}

export const ActivityRuleDetailContent: React.FC<ActivityRuleDetailContentProps> = ({ detail }) => {
  return (
    <>
      <Descriptions column={2} bordered>
        <Descriptions.Item label="RuleId">{detail.id}</Descriptions.Item>
        <Descriptions.Item label="活动类型">
          {contestTypeMapColumn.get(detail.activityType || 0) || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="活动主题(英语)">{detail.activityTopicEn}</Descriptions.Item>
        <Descriptions.Item label="活动主题(日语)">{detail.activityTopicJa}</Descriptions.Item>
        <Descriptions.Item label="开始时间">
          {detail.startTime
            ? dayjs(detail.startTime).format('YYYY-MM-DD HH:mm')
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="结束时间">
          {detail.endTime
            ? dayjs(detail.endTime).format('YYYY-MM-DD HH:mm')
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="状态">
          <Tag color={STATUS_MAP[detail.status || 0]?.color}>
            {STATUS_MAP[detail.status || 0]?.text}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="创建人">{detail.updateUserName || '-'}</Descriptions.Item>
        <Descriptions.Item label="描述(英语)" span={2}>
          {detail.descriptionEn || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="描述(日语)" span={2}>
          {detail.descriptionJa || '-'}
        </Descriptions.Item>
      </Descriptions>

      <div style={{ marginTop: 24 }}>
        <Title level={4}>奖品设置</Title>
        {detail.rewardSettings && detail.rewardSettings.length > 0 ? (
          <div>
            {detail.rewardSettings.map((item, index) => (
              <div key={index} style={{ marginBottom: 8 }}>
                <Paragraph>
                  <strong>设置{index + 1}:</strong>
                </Paragraph>
                <Paragraph>英语: {item.descriptionEn}</Paragraph>
                <Paragraph>日语: {item.descriptionJa}</Paragraph>
              </div>
            ))}
          </div>
        ) : (
          <Paragraph>-</Paragraph>
        )}
      </div>

      <div style={{ marginTop: 24 }}>
        <Title level={4}>奖励发放说明</Title>
        {detail.rewardDistribution && detail.rewardDistribution.length > 0 ? (
          <div>
            {detail.rewardDistribution.map((item, index) => (
              <div key={index} style={{ marginBottom: 8 }}>
                <Paragraph>
                  <strong>说明{index + 1}:</strong>
                </Paragraph>
                <Paragraph>英语: {item.descriptionEn}</Paragraph>
                <Paragraph>日语: {item.descriptionJa}</Paragraph>
              </div>
            ))}
          </div>
        ) : (
          <Paragraph>-</Paragraph>
        )}
      </div>

      <div style={{ marginTop: 24 }}>
        <Title level={4}>参赛作品要求</Title>
        {detail.submissionRequirements && detail.submissionRequirements.length > 0 ? (
          <div>
            {detail.submissionRequirements.map((item, index) => (
              <div key={index} style={{ marginBottom: 8 }}>
                <Paragraph>● {item.requirementJa || item.requirementEn}</Paragraph>
              </div>
            ))}
          </div>
        ) : (
          <Paragraph>-</Paragraph>
        )}
      </div>

      <div style={{ marginTop: 24 }}>
        <Title level={4}>获奖作品评判标准</Title>
        {detail.awardCriteria && detail.awardCriteria.length > 0 ? (
          <div>
            {detail.awardCriteria.map((item, index) => (
              <div key={index} style={{ marginBottom: 8 }}>
                <Paragraph>● {item.criterionJa || item.criterionEn}</Paragraph>
              </div>
            ))}
          </div>
        ) : (
          <Paragraph>-</Paragraph>
        )}
      </div>

      <div style={{ marginTop: 24 }}>
        <Title level={4}>常见问题</Title>
        {detail.commonQuestions && detail.commonQuestions.length > 0 ? (
          <div>
            {detail.commonQuestions.map((item, index) => (
              <div key={index} style={{ marginBottom: 16, border: '1px solid #d9d9d9', padding: 16, borderRadius: 4 }}>
                <Paragraph>
                  <strong>问题{index + 1}:</strong>
                </Paragraph>
                <Paragraph>标题(英语): {item.titleEn}</Paragraph>
                <Paragraph>标题(日语): {item.titleJa}</Paragraph>
                <Paragraph>内容(英语): {item.contentEn}</Paragraph>
                <Paragraph>内容(日语): {item.contentJa}</Paragraph>
              </div>
            ))}
          </div>
        ) : (
          <Paragraph>-</Paragraph>
        )}
      </div>

      <div style={{ marginTop: 24 }}>
        <Title level={4}>重要提示</Title>
        {detail.importantNotes && detail.importantNotes.length > 0 ? (
          <div>
            {detail.importantNotes.map((item, index) => (
              <div key={index} style={{ marginBottom: 8 }}>
                <Paragraph>● {item.noteJa || item.noteEn}</Paragraph>
              </div>
            ))}
          </div>
        ) : (
          <Paragraph>-</Paragraph>
        )}
      </div>
    </>
  );
};

const ActivityRuleDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<ActivityRule | null>(null);

  useEffect(() => {
    if (id) {
      setLoading(true);
      getActivityRuleDetail(Number(id))
        .then((res) => {
          if (res.status === 200 && res.data) {
            setDetail(res.data);
          } else {
            message.error('获取详情失败');
          }
        })
        .catch(() => {
          message.error('获取详情失败');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id]);

  if (!detail) {
    return <Card loading={loading}>加载中...</Card>;
  }

  return (
    <Card
      title="活动规则详情"
      extra={
        <Button onClick={() => navigate(-1)}>返回</Button>
      }
    >
      <ActivityRuleDetailContent detail={detail} />
    </Card>
  );
};

export default ActivityRuleDetailPage;

